import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import {
  ButtonClickedEvent,
  LinkClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import { Hero } from '@atlassian/mpac-home';
import { Meta } from '@atlassian/mpac-primitives';
import { usePageRouteSourceProvider } from '@atlassian/mpac-ui';
import { ForgeWidget, ScrollPinnedSections, StatsSection } from '@atlassian/mpac-widgets';
import { css } from '@compiled/react';
import React, { useEffect } from 'react';

import {
  ANALYTICS_SUBJECT_ID_FORGE_WIDGET_BUTTON,
  ANALYTICS_SUBJECT_ID_HERO_BANNER_CTA,
} from '../../../../analytics/Events';
import useCTAClickHandler from '../../../../hooks/useCTAClickHandler';
import AIInfoPinnedScrollWidgetConfig from '../config/aIInfoPinnedScrollWidget.config';
import ForgeWidgetConfig from '../config/forgeWidget.config';
import HeroSectionConfig from '../config/heroSection.config';
import { useStatsSectionConfig } from '../config/statsSection.config';
import { PAGE_ELEMENT_IDS } from '../utils/pageElementIds';
import { useAgentsPageEngagementAnalytics } from '../utils/useAgentsPageEngagementAnalytics';
import { AIAppsCollections, RovoAppsCollections } from './Collections';
import { MeetRovoSection } from './MeetRovoSection';
import { AgentsStructuredData } from './StructuredData';

// Page metadata constants
const title = HeroSectionConfig.heading;
const description = AIInfoPinnedScrollWidgetConfig.subHeading;
const canonicalUrl = 'https://marketplace.atlassian.com/ai-hub';
const image = HeroSectionConfig.heroDesktopLeftImage; // Using the hero image for social sharing

const heroWrapperStyle = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: '110rem',
  margin: 'auto',
  alignSelf: 'stretch',
  flexDirection: 'column',
  justifyContent: 'center',
  marginBottom: token('space.1000'),
  [media.below.sm]: {
    marginBottom: token('space.400'),
  },
});

const aiInfoPinnedScrollWidgetStyle = css({
  marginBottom: token('space.1000'),
  [media.below.md]: {
    marginBottom: token('space.800'),
  },
});

const aiAppsCollectionsStyle = css({
  // no nearby design token available for these margins
  marginBottom: '9.5rem',
  [media.below.md]: {
    marginBottom: '6.75rem',
  },
  [media.below.sm]: {
    marginBottom: '4.5rem',
  },
});

const meetRovoSectionStyle = css({
  marginBottom: token('space.1000'),
});

const rovoAppsCollectionsStyle = css({
  marginBottom: '8.687rem',
  [media.below.md]: {
    marginBottom: token('space.1000'),
  },
  [media.below.sm]: {
    marginBottom: '6.25rem',
  },
});

const statsSectionStyle = css({
  marginBottom: token('space.600'),
  [media.below.md]: {
    marginBottom: token('space.1000'),
  },
  [media.below.sm]: {
    marginBottom: token('space.800'),
  },
});

const forgeWidgetStyle = css({
  marginBottom: token('space.800'),
  [media.below.md]: {
    marginBottom: token('space.400'),
  },
});

const AgentsPage = () => {
  useAgentsPageEngagementAnalytics();
  const { setPageRouteSource } = usePageRouteSourceProvider();
  const statsSectionConfig = useStatsSectionConfig();
  const fireEvent = useAnalyticsEvents();
  const handleCTAClick = useCTAClickHandler();

  useEffect(() => {
    setPageRouteSource('agentsPage');
  }, [setPageRouteSource]);

  const structuredDataProps = {
    title,
    description,
    canonicalUrl,
    image,
    installs: statsSectionConfig.bigStat.stat,
  };

  return (
    <>
      <Meta title={title} description={description} canonicalUrl={canonicalUrl} image={image} />
      <AgentsStructuredData {...structuredDataProps} />
      <div css={heroWrapperStyle} id={PAGE_ELEMENT_IDS.HERO}>
        <Hero
          {...HeroSectionConfig}
          onCTAClick={(event) =>
            handleCTAClick(
              event,
              ButtonClickedEvent,
              ANALYTICS_SUBJECT_ID_HERO_BANNER_CTA,
              {
                buttonText: HeroSectionConfig.buttonLabel,
                destinationUrl: HeroSectionConfig.buttonHref,
              },
              HeroSectionConfig.buttonHref
            )
          }
        />
      </div>
      <div css={aiInfoPinnedScrollWidgetStyle} id={PAGE_ELEMENT_IDS.AI_INFO_PINNED_SCROLL_WIDGET}>
        <ScrollPinnedSections
          {...AIInfoPinnedScrollWidgetConfig}
          onProductCTAClick={(event, product) =>
            handleCTAClick(
              event,
              LinkClickedEvent,
              product.productCTA?.analytics?.actionSubjectId || '',
              {
                ...(product.productCTA?.analytics?.attributes || {}),
                destinationUrl: product.productCTA?.url,
              },
              product.productCTA?.url
            )
          }
        />
      </div>
      <div css={aiAppsCollectionsStyle} id={PAGE_ELEMENT_IDS.AI_APPS_COLLECTIONS}>
        <AIAppsCollections />
      </div>
      <div css={meetRovoSectionStyle} id={PAGE_ELEMENT_IDS.MEET_ROVO}>
        <MeetRovoSection />
      </div>
      <div css={rovoAppsCollectionsStyle} id={PAGE_ELEMENT_IDS.ROVO_APPS_COLLECTIONS}>
        <RovoAppsCollections />
      </div>
      <div css={statsSectionStyle} id={PAGE_ELEMENT_IDS.STATS_SECTION}>
        <StatsSection {...statsSectionConfig} />
      </div>
      <div css={forgeWidgetStyle} id={PAGE_ELEMENT_IDS.FORGE_WIDGET}>
        <ForgeWidget
          {...ForgeWidgetConfig}
          onClick={() =>
            fireEvent({
              eventType: LinkClickedEvent,
              actionSubjectId: ANALYTICS_SUBJECT_ID_FORGE_WIDGET_BUTTON,
              attributes: {
                destinationUrl: ForgeWidgetConfig.buttonHref,
                linkText: ForgeWidgetConfig.buttonLabel,
              },
            })
          }
        />
      </div>
    </>
  );
};

export default AgentsPage;
